<template>
  <ContentWrap>
    <el-form ref="queryFormRef" :inline="true" :model="queryParams" class="-mb-15px" label-width="80px">
      <el-form-item label="酒店名称" prop="shopName">
        <el-input
          v-model="queryParams.shopName"
          placeholder="请输入酒店名称"
          clearable
          class="!w-240px"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="店铺状态" prop="shopStatus">
        <el-select v-model="queryParams.shopStatus" placeholder="全部状态" clearable class="!w-200px">
          <el-option
            v-for="item in shopStatusOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleQuery">
          <Icon class="mr-5px" icon="ep:search" />
          搜索
        </el-button>
        <el-button @click="resetQuery">
          <Icon class="mr-5px" icon="ep:refresh" />
          重置
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-alert
      class="mb-16px"
      type="info"
      :closable="false"
      title="酒店列表为平台专用视图：仅展示酒店业态店铺。状态调整、上下架、冻结与平台代运营由店铺生命周期接口提供，当前版本仅开放查询与详情。"
    />
    <el-table v-loading="loading" :data="list">
      <el-table-column label="酒店名称" prop="shopName" min-width="200" show-overflow-tooltip />
      <el-table-column label="店铺状态" prop="shopStatus" width="110" align="center">
        <template #default="scope">
          <el-tag :type="statusTagType(scope.row.shopStatus)">
            {{ statusLabel(scope.row.shopStatus) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="评分" prop="score" width="120" align="center">
        <template #default="scope">{{ scope.row.score != null ? scope.row.score : '-' }}</template>
      </el-table-column>
      <el-table-column label="客服电话" prop="contactPhone" width="150" align="center">
        <template #default="scope">{{ scope.row.contactPhone || '-' }}</template>
      </el-table-column>
      <el-table-column label="运营模式" width="120" align="center">
        <template #default="scope">{{ operationModeLabel(scope.row.operationMode) }}</template>
      </el-table-column>
      <el-table-column label="结算周期" width="110" align="center">
        <template #default="scope">{{ settleCycleLabel(scope.row.settleCycle) }}</template>
      </el-table-column>
      <el-table-column label="自动结算" width="100" align="center">
        <template #default="scope">{{ scope.row.autoSettle === 1 ? '是' : '否' }}</template>
      </el-table-column>
      <el-table-column label="操作" width="100" fixed="right" align="center">
        <template #default="scope">
          <el-button
            v-hasPermi="['hotel:platform:shop:detail']"
            link
            type="primary"
            @click="openDetail(scope.row.shopId)"
          >
            详情
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <el-drawer v-model="detailVisible" title="酒店详情" size="480px">
    <div v-loading="detailLoading">
      <el-descriptions :column="1" border>
        <el-descriptions-item label="酒店名称">{{ detail.shopName || '-' }}</el-descriptions-item>
        <el-descriptions-item label="店铺编号">{{ detail.shopId }}</el-descriptions-item>
        <el-descriptions-item label="店铺状态">
          <el-tag :type="statusTagType(detail.shopStatus)">{{ statusLabel(detail.shopStatus) }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="店铺类型">{{ shopTypeLabel(detail.shopType) }}</el-descriptions-item>
        <el-descriptions-item label="评分">{{ detail.score != null ? detail.score : '-' }}</el-descriptions-item>
        <el-descriptions-item label="客服电话">{{ detail.contactPhone || '-' }}</el-descriptions-item>
        <el-descriptions-item label="运营模式">{{ operationModeLabel(detail.operationMode) }}</el-descriptions-item>
        <el-descriptions-item label="结算周期">{{ settleCycleLabel(detail.settleCycle) }}</el-descriptions-item>
        <el-descriptions-item label="自动结算">{{ detail.autoSettle === 1 ? '是' : '否' }}</el-descriptions-item>
        <el-descriptions-item label="入住 / 退房时间">
          {{ detail.checkInTime || '-' }} / {{ detail.checkOutTime || '-' }}
        </el-descriptions-item>
        <el-descriptions-item label="房间数">{{ detail.roomCount != null ? detail.roomCount : '-' }}</el-descriptions-item>
        <el-descriptions-item label="订单数">{{ detail.orderCount != null ? detail.orderCount : '-' }}</el-descriptions-item>
      </el-descriptions>
    </div>
  </el-drawer>
</template>

<script lang="ts" setup>
import * as PlatformShopApi from '@/api/hotel/platformShop'
import type { HotelPlatformShopVO } from '@/api/hotel/platformShop'

defineOptions({ name: 'HotelPlatformShop' })

const loading = ref(true)
const list = ref<HotelPlatformShopVO[]>([])
const total = ref(0)
const queryFormRef = ref()

const queryParams = reactive({
  pageNo: 1,
  pageSize: 20,
  shopName: undefined as string | undefined,
  shopStatus: undefined as number | undefined
})

const shopStatusOptions = [
  { value: 0, label: '未激活' },
  { value: 1, label: '正常' },
  { value: 2, label: '冻结' },
  { value: 3, label: '关店中' },
  { value: 4, label: '已关闭' }
]

const statusLabel = (status?: number) =>
  shopStatusOptions.find((item) => item.value === status)?.label || '-'
const statusTagType = (status?: number) => {
  if (status === 1) return 'success'
  if (status === 2) return 'warning'
  if (status === 4) return 'danger'
  return 'info'
}
const operationModeLabel = (mode?: string) => {
  if (mode === 'SELF_OPERATED') return '自营'
  if (mode === 'PLATFORM_MANAGED') return '平台托管'
  if (mode === 'THIRD_PARTY') return '第三方入驻'
  return '-'
}
const settleCycleLabel = (cycle?: string) => {
  const labels: Record<string, string> = {
    INSTANT: '实时结算',
    DAILY: '日结',
    WEEKLY: '周结',
    MONTHLY: '月结'
  }
  return cycle ? labels[cycle] || cycle : '-'
}
const shopTypeLabel = (type?: number) => {
  const labels: Record<number, string> = { 0: '第三方', 1: '平台自营', 2: '联营', 3: '酒店' }
  return type != null ? labels[type] || String(type) : '-'
}

const getList = async () => {
  loading.value = true
  try {
    const data = await PlatformShopApi.getPlatformShopPage({
      pageNo: queryParams.pageNo,
      pageSize: queryParams.pageSize,
      shopName: queryParams.shopName || undefined,
      shopStatus: queryParams.shopStatus
    })
    list.value = data?.list || []
    total.value = data?.total || 0
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryParams.shopName = undefined
  queryParams.shopStatus = undefined
  queryFormRef.value?.resetFields()
  handleQuery()
}

// 详情
const detailVisible = ref(false)
const detailLoading = ref(false)
const detail = ref<Partial<HotelPlatformShopVO>>({})

const openDetail = async (shopId: string | number) => {
  detailVisible.value = true
  detailLoading.value = true
  try {
    detail.value = (await PlatformShopApi.getPlatformShopDetail(shopId)) || {}
  } finally {
    detailLoading.value = false
  }
}

onMounted(() => {
  getList()
})
</script>
